import type { Task } from "@engram/core";
import { ArrowDownUp, Check, ChevronRight, CirclePlus, FileText, Flag, ListFilter, Repeat, Search, X } from "lucide-react";
import { Fragment, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { setSetting, toggleTask } from "../db/actions";
import { useQuery, useRow } from "../db/client";
import { collator, shortDate, dateFmt } from "./format";
import { Popover, useAnchor } from "./Menu";
import { usePrefs } from "./prefs";
import { openTaskDialog } from "./TaskDialog";

type Tab = "mine" | "notebooks" | "notes" | "today";
type Due = "any" | "overdue" | "today" | "week" | "none";
type Sort = "relevance" | "title" | "due" | "created" | "updated";

type Row = Task & { note_title: string; notebook_id: string; notebook: string };

const DAY = 86400000;

function startOfDay(ms = Date.now()) {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const SORTS: [Sort, string][] = [
  ["relevance", "Relevance"],
  ["title", "Task title"],
  ["due", "Due date"],
  ["created", "Created"],
  ["updated", "Updated"],
];

/** Evernote's Tasks page: tabs, New Task, filter and sort menus, "Find tasks…", and a table of every task. */
export function TasksPage() {
  const [tab, setTab] = useState<Tab>("mine");
  const [q, setQ] = useState("");
  const showCompleted = usePrefs((s) => s.showCompletedTasks);
  const setShowCompleted = (v: boolean) => usePrefs.getState().set({ showCompletedTasks: v });
  const [only, setOnly] = useState<{ recurring: boolean; flagged: boolean; completed: boolean }>({ recurring: false, flagged: false, completed: false });
  const [due, setDue] = useState<Due>("any");
  const [sort, setSort] = useState<{ key: Sort; desc: boolean }>({ key: "relevance", desc: false });
  const [completedLast, setCompletedLast] = useState(true);
  const [pickNote, setPickNote] = useState(false);
  const filter = useAnchor();
  const sortMenu = useAnchor();
  const [, navigate] = useLocation();

  const rows = useQuery<Row>(
    `SELECT t.*, n.title AS note_title, n.notebook_id, nb.name AS notebook
     FROM tasks t JOIN notes n ON n.id = t.note_id LEFT JOIN notebooks nb ON nb.id = n.notebook_id
     WHERE t.deleted = 0 AND n.deleted = 0 AND n.trashed_at IS NULL
     ORDER BY (t.due_at IS NULL), t.due_at, t.position, t.created`,
    [],
    ["tasks", "notes", "notebooks"],
  );

  const today = startOfDay();
  const term = q.trim().toLowerCase();
  const list = (rows ?? []).filter((t) => {
    const isDone = t.completed_at != null;
    if (!showCompleted && isDone && !only.completed) return false;
    if (only.completed && !isDone) return false;
    if (only.flagged && !t.flagged) return false;
    if (only.recurring && !t.recurrence) return false;
    if (term && !t.title.toLowerCase().includes(term) && !t.note_title.toLowerCase().includes(term)) return false;
    const d = t.due_at;
    if (tab === "today" && !(d != null && d < today + DAY && !isDone)) return false;
    switch (due) {
      case "overdue":
        // An all-day task is due for the whole day (stored at midnight), so it isn't overdue until tomorrow.
        return d != null && d < (t.due_all_day ? today : Date.now()) && !isDone;
      case "today":
        return d != null && d >= today && d < today + DAY;
      case "week":
        return d != null && d >= today && d < today + 7 * DAY;
      case "none":
        return d == null;
      default:
        return true;
    }
  });
  const dir = sort.desc ? -1 : 1;
  const by: Record<Sort, (a: Row, b: Row) => number> = {
    relevance: () => 0, // the query's order: due first, soonest first
    title: (a, b) => collator.compare(a.title, b.title) * dir, // Latin before Hangul, like Evernote
    due: (a, b) => ((a.due_at ?? Infinity) - (b.due_at ?? Infinity)) * dir || 0,
    created: (a, b) => (a.created - b.created) * dir,
    updated: (a, b) => (a.updated - b.updated) * dir,
  };
  list.sort((a, b) => (completedLast ? Number(a.completed_at != null) - Number(b.completed_at != null) : 0) || by[sort.key](a, b));

  const groups: [string, string, Row[]][] =
    tab === "notes" || tab === "notebooks"
      ? [...Map.groupBy(list, (t) => (tab === "notes" ? t.note_id : t.notebook_id)).entries()].map(([k, ts]) => [k, tab === "notes" ? ts[0]!.note_title || "Untitled" : (ts[0]!.notebook ?? ""), ts])
      : [["", "", list]];
  const activeFilters = Number(only.recurring) + Number(only.flagged) + Number(only.completed) + Number(due !== "any") + Number(!showCompleted);

  return (
    <div className="page tasks-page">
      <div className="page-header row">
        <h1>
          Tasks <span className="title-count">{rows ? rows.length : ""}</span>
        </h1>
      </div>
      <div className="tasks-bar">
        <div className="tabs">
          {(
            [
              ["mine", "My tasks"],
              ["notebooks", "Notebooks"],
              ["notes", "Notes"],
              ["today", "Today"],
            ] as [Tab, string][]
          ).map(([k, label]) => (
            <button key={k} className={`tab${tab === k ? " active" : ""}`} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>
        <div className="grow" />
        <button className="btn outline-pill" onClick={() => openTaskDialog()}>
          <CirclePlus size={15} className="ic-task" /> New Task
        </button>
        <button className={`icon-btn${activeFilters ? " on" : ""}`} title="Filter tasks" onClick={filter.open}>
          <ListFilter size={16} />
          {activeFilters > 0 && <span className="badge">{activeFilters}</span>}
        </button>
        <button className="icon-btn" title="Sort and options" onClick={sortMenu.open}>
          <ArrowDownUp size={16} />
        </button>
        <label className="find-input tasks-find">
          <input placeholder="Find tasks…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q ? (
            <button className="icon-btn" aria-label="Clear" onClick={() => setQ("")}><X size={13} /></button>
          ) : (
            <Search size={14} />
          )}
        </label>
      </div>
      <table className="table tasks-table">
        <thead>
          <tr>
            <th>Title</th>
            <th className="col-date">Due date</th>
            <th className="col-note">Assigned note</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(([key, label, ts]) => (
            <Fragment key={key || "all"}>
              {label && (
                <tr className="row-group">
                  <td colSpan={3}>
                    {label} <span className="row-count">{ts.length}</span>
                  </td>
                </tr>
              )}
              {ts.map((t) => {
                const done = t.completed_at != null;
                const overdue = !done && t.due_at != null && (t.due_all_day ? t.due_at < today : t.due_at < Date.now());
                return (
                  <tr key={t.id} className={`task-row${done ? " done" : ""}`} onClick={() => openTaskDialog(t.id)}>
                    <td>
                      <button
                        className={`task-check${done ? " checked" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          void toggleTask(t.id);
                        }}
                        aria-label={done ? "Mark incomplete" : "Mark complete"}
                      />
                      <span className="task-row-title">{t.title || <span className="muted">Untitled task</span>}</span>
                      {t.recurrence && <Repeat size={12} className="muted" />}
                      {t.flagged ? <Flag size={13} className="task-flag" /> : null}
                    </td>
                    <td className={`col-date${overdue ? " overdue" : ""}`}>
                      {t.due_at != null ? `${shortDate(t.due_at)}${t.due_all_day ? "" : `, ${dateFmt({ hour: "numeric", minute: "2-digit" }).format(t.due_at)}`}` : "–"}
                    </td>
                    <td className="col-note">
                      <button
                        className="row-title"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/note/${t.note_id}`);
                        }}
                      >
                        <FileText size={13} /> <span>{t.note_title || "Untitled"}</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
      {rows && list.length === 0 && <div className="list-empty">{tab === "today" ? "Nothing due today" : activeFilters || term ? "No tasks match" : "No tasks"}</div>}

      {filter.anchor && (
        <Popover anchor={filter.anchor} onClose={filter.close} align="end" className="menu">
          <div className="menu-heading">Filter by</div>
          {(
            [
              ["recurring", "Recurring", <Repeat size={14} />],
              ["flagged", "Flagged", <Flag size={14} />],
              ["completed", "Completed", <Check size={14} />],
            ] as const
          ).map(([k, label, icon]) => (
            <button key={k} className="menu-item" onClick={() => setOnly((o) => ({ ...o, [k]: !o[k] }))}>
              <span className="menu-icon">{icon}</span>
              <span className="menu-label">{label}</span>
              {only[k] && <span className="menu-check">✓</span>}
            </button>
          ))}
          <DueSubmenu value={due} onChange={setDue} />
          <div className="menu-sep" />
          <div className="menu-heading">Include</div>
          <button className="menu-item" onClick={() => setShowCompleted(!showCompleted)}>
            <span className="menu-icon" />
            <span className="menu-label">Completed</span>
            {showCompleted && <span className="menu-check">✓</span>}
          </button>
          {activeFilters > 0 && (
            <>
              <div className="menu-sep" />
              <button
                className="menu-item"
                onClick={() => {
                  setOnly({ recurring: false, flagged: false, completed: false });
                  setDue("any");
                  setShowCompleted(true);
                }}
              >
                <span className="menu-icon"><X size={14} /></span>
                <span className="menu-label">Clear filters</span>
              </button>
            </>
          )}
        </Popover>
      )}

      {sortMenu.anchor && (
        <Popover anchor={sortMenu.anchor} onClose={sortMenu.close} align="end" className="menu">
          <div className="menu-heading">Sort by</div>
          {SORTS.map(([k, label]) => (
            <button key={k} className="menu-item" onClick={() => setSort((s) => (s.key === k && k !== "relevance" ? { key: k, desc: !s.desc } : { key: k, desc: k === "created" || k === "updated" }))}>
              <span className="menu-icon">{sort.key === k && <Check size={14} />}</span>
              <span className="menu-label">{label}</span>
              {sort.key === k && k !== "relevance" && <span className="menu-hint">{sort.desc ? "↓" : "↑"}</span>}
            </button>
          ))}
          <div className="menu-sep" />
          <button className="menu-item" onClick={() => setCompletedLast((v) => !v)}>
            <span className="menu-icon" />
            <span className="menu-label">Move completed to bottom</span>
            {completedLast && <span className="menu-check">✓</span>}
          </button>
          <div className="menu-sep" />
          <button className="menu-item" onClick={() => (sortMenu.close(), setPickNote(true))}>
            <span className="menu-icon" />
            <span className="menu-label">Default task note…</span>
          </button>
        </Popover>
      )}
      {pickNote && <DefaultTaskNoteDialog onClose={() => setPickNote(false)} />}
    </div>
  );
}

function DueSubmenu({ value, onChange }: { value: Due; onChange: (d: Due) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="menu-item" onClick={() => setOpen((v) => !v)}>
        <span className="menu-icon" />
        <span className="menu-label">Due date</span>
        {value !== "any" && <span className="menu-hint">{DUE_LABEL[value]}</span>}
        <ChevronRight size={14} className={`muted chev${open ? " open" : ""}`} />
      </button>
      {open &&
        (Object.keys(DUE_LABEL) as Due[]).map((k) => (
          <button key={k} className="menu-item sub" onClick={() => onChange(k)}>
            <span className="menu-icon">{value === k && <Check size={14} />}</span>
            <span className="menu-label">{DUE_LABEL[k]}</span>
          </button>
        ))}
    </>
  );
}

const DUE_LABEL: Record<Due, string> = { any: "Any time", overdue: "Overdue", today: "Today", week: "Next 7 days", none: "No due date" };

/** Evernote's "Select default task note": tasks made with New Task go here unless another note is picked. */
export function DefaultTaskNoteDialog({ onClose }: { onClose: () => void }) {
  const current = useRow<{ value: string }>("SELECT value FROM settings WHERE id = 'defaultTaskNote' AND deleted = 0", [], ["settings"]);
  const currentId = current ? (JSON.parse(current.value) as string) : null;
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const currentNote = useRow<{ id: string; title: string }>(currentId ? "SELECT id, title FROM notes WHERE id = ?" : null, [currentId], ["notes"]);
  const notes = useQuery<{ id: string; title: string; nb: string | null }>(
    `SELECT n.id, n.title, nb.name AS nb FROM notes n LEFT JOIN notebooks nb ON nb.id = n.notebook_id
     WHERE n.deleted = 0 AND n.trashed_at IS NULL AND n.title LIKE ? ORDER BY n.updated DESC LIMIT 60`,
    [`%${q.trim()}%`],
    ["notes"],
  );
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal location-dialog" role="dialog" aria-label="Select default task note" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <div className="location-head">
          <h2>Select default task note</h2>
          <div className="grow" />
          <button className="icon-btn" aria-label="Close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body muted">Tasks you add from the create button will be added here, unless you choose a different note.</div>
        <label className="find-input location-find">
          <input autoFocus placeholder="Find note…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Search size={14} />
        </label>
        <div className="location-list">
          {currentNote && !q && (
            <div className="location-row current">
              <Check size={15} />
              <span>{currentNote.title || "Untitled"} (current)</span>
            </div>
          )}
          {(notes ?? [])
            .filter((n) => n.id !== currentId)
            .map((n) => (
              <button key={n.id} className={`location-row${picked === n.id ? " picked" : ""}`} onClick={() => setPicked(n.id)} onDoubleClick={() => (void setSetting("defaultTaskNote", n.id), onClose())}>
                <FileText size={15} />
                <span>{n.title || "Untitled"}</span>
                {n.nb && <span className="muted">{n.nb}</span>}
              </button>
            ))}
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!picked} onClick={() => (void setSetting("defaultTaskNote", picked), onClose())}>Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
